import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useRebaseClient } from "@rebasepro/app";
import { useSideDialogsController, useUrlController } from "@rebasepro/cms";
import type { RebaseClient } from "@rebasepro/types";
import {
    Alert as RebaseAlert,
    Button as RebaseButton,
    Chip as RebaseChip,
    CircularProgressCenter as RebaseCircularProgressCenter,
    IconButton as RebaseIconButton,
    Separator as RebaseSeparator,
    Tooltip as RebaseTooltip,
    Typography as RebaseTypography,
    ExternalLinkIcon as RebaseExternalLinkIcon,
    Maximize2Icon as RebaseMaximize2Icon,
    XIcon as RebaseXIcon,
    cls,
    iconSize
} from "@rebasepro/ui";
import {
    Archive as RebaseArchiveIcon,
    ArchiveRestore as RebaseUnarchiveIcon,
    Check as RebaseCheckIcon,
    Reply as RebaseReplyIcon,
    UserRound as RebaseUserIcon
} from "lucide-react";

import { EmailComposer, type ComposerTarget } from "./EmailComposer";
import { EmailThreadView } from "./EmailThreadView";
import { EntityLink, entityIdOf, entityLinkCls } from "./_links";
import { useAnnouncedSnackbar } from "./_workspace";
import {
    ENTITY_COLOR,
    ENTITY_LABEL,
    KIND_LABEL,
    OPEN_CONVERSATION_EVENT,
    callFunction,
    conversationUrl,
    daysWaiting,
    entityTypeOf,
    gmailThreadUrl,
    isPersonThread,
    kindOf,
    markThreadRead,
    sinceLabel,
    threadTitle,
    threadWith,
    type MailboxStatus,
    type MessageRow,
    type ThreadRow
} from "./_email_shared";
import { plural } from "../plural";

const Alert = RebaseAlert as React.ComponentType<any>;
const Button = RebaseButton as React.ComponentType<any>;
const Chip = RebaseChip as React.ComponentType<any>;
const CircularProgressCenter = RebaseCircularProgressCenter as React.ComponentType<any>;
const IconButton = RebaseIconButton as React.ComponentType<any>;
const Separator = RebaseSeparator as React.ComponentType<any>;
const Tooltip = RebaseTooltip as React.ComponentType<any>;
const Typography = RebaseTypography as React.ComponentType<any>;
const ExternalLinkIcon = RebaseExternalLinkIcon as React.ComponentType<any>;
const Maximize2Icon = RebaseMaximize2Icon as React.ComponentType<any>;
const XIcon = RebaseXIcon as React.ComponentType<any>;
const ArchiveIcon = RebaseArchiveIcon as React.ComponentType<any>;
const UnarchiveIcon = RebaseUnarchiveIcon as React.ComponentType<any>;
const CheckIcon = RebaseCheckIcon as React.ComponentType<any>;
const ReplyIcon = RebaseReplyIcon as React.ComponentType<any>;
const UserIcon = RebaseUserIcon as React.ComponentType<any>;

/**
 * One conversation, over whatever screen you are on.
 *
 * A conversation shown anywhere but Correo — the "Correo" block on a
 * candidate's or a company's cover, the Correo tab on their record, a reply on
 * an employer's timeline — was either a link that left the screen for the
 * mailbox, or a reader that unfolded under the list with no way to answer.
 * Leaving threw away where you were, and the mailbox then opened on the top
 * of its queue instead of on the conversation you clicked (see InboxView's
 * first-selection effects). Reading in place meant that answering still meant
 * leaving.
 *
 * So every one of those opens this: the same side panel a candidate or a
 * vacancy opens in (EntityLink), holding what Correo's reading pane holds —
 * the messages, the reply box, archive, and "no hace falta contestar". Closing
 * it leaves the screen where it was.
 *
 * What it leaves to Correo is triage: connecting the conversation to somebody
 * else, silencing a sender, who owns it and when to come back to it. Those are
 * decisions made across the mailbox, not about one person, and the expand
 * button in the bar goes there, open on this conversation.
 *
 * ── The address bar ──────────────────────────────────────────────────
 *
 * While it is open the URL is `conversationUrl(id)` — the same trick the
 * record panels play, with the screen underneath kept by the router's
 * `base_location`. Back closes the panel rather than leaving the record, and a
 * reload or a copied link opens Correo on this conversation.
 */

/** Wide enough for the reader's `max-w-3xl`, narrower than a record. */
const PANEL_WIDTH = "min(880px, 100vw)";

/** The same cap the mailbox reads a conversation with. */
const MESSAGE_LIMIT = 200;

/**
 * Open a conversation in the side panel.
 *
 * `onChanged` runs after anything the panel wrote — read, answered, archived —
 * so the list it was opened from can reload rather than keep saying "esperando
 * respuesta" about a conversation somebody just answered.
 */
export function useOpenConversation(onChanged?: () => void) {
    const sideDialogs = useSideDialogsController();
    return useCallback((threadId: string) => {
        sideDialogs.open({
            key: `conversacion/${threadId}`,
            // Keyed by thread: side dialogs are keyed by stack position, so a
            // panel opened where another just closed reuses its component —
            // and would show the previous conversation's messages under the
            // new one's subject until its own arrived.
            component: <ConversationPanel key={threadId} threadId={threadId} onChanged={onChanged} />,
            width: PANEL_WIDTH,
            urlPath: conversationUrl(threadId)
        });
    }, [sideDialogs, onChanged]);
}

/** A conversation, named, and openable — drawn exactly like an `EntityLink`. */
export function ConversationLink({
    id,
    children,
    onChanged,
    className
}: {
    id: string;
    children: React.ReactNode;
    onChanged?: () => void;
    className?: string;
}) {
    const open = useOpenConversation(onChanged);
    return (
        <button
            type="button"
            className={cls(entityLinkCls, className)}
            onClick={(e) => {
                // Inside a clickable card, opening the conversation must not
                // also select the card.
                e.stopPropagation();
                open(id);
            }}
        >
            {children}
            <ExternalLinkIcon size={iconSize.smallest} className="opacity-60 shrink-0" />
        </button>
    );
}

export function ConversationPanel({ threadId, onChanged }: { threadId: string; onChanged?: () => void }) {
    const client = useRebaseClient<RebaseClient>();
    const data = (client as any)?.data;
    const sideDialogs = useSideDialogsController();
    const urls = useUrlController();
    const { snackbar, liveRegion } = useAnnouncedSnackbar();

    /** `undefined` while it is being read, `null` once it is known not to exist. */
    const [thread, setThread] = useState<ThreadRow | null | undefined>(undefined);
    const [messages, setMessages] = useState<MessageRow[] | null>(null);
    const [status, setStatus] = useState<MailboxStatus | null>(null);
    const [linkedName, setLinkedName] = useState<string | null>(null);
    const [vacancyTitle, setVacancyTitle] = useState<string | null>(null);
    const [error, setError] = useState("");
    const [replying, setReplying] = useState(false);
    const [working, setWorking] = useState(false);

    const load = useCallback(async () => {
        if (!data) return;
        setError("");
        try {
            const [row, page] = await Promise.all([
                data.collection("email-threads").findById(threadId),
                data.collection("email-messages").find({
                    where: { thread_id: ["==", threadId] },
                    orderBy: ["sent_at", "asc"],
                    limit: MESSAGE_LIMIT
                })
            ]);
            setThread((row as ThreadRow | undefined) ?? null);
            setMessages((page?.data ?? []) as MessageRow[]);
        } catch (err: any) {
            setError(err?.message || "No se pudo leer la conversación");
            setMessages(current => current ?? []);
        }
    }, [data, threadId]);

    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        callFunction<MailboxStatus>(client, "sync-inbox")
            .then(setStatus)
            // Only the "can I send" state depends on it; the reader does not.
            .catch(() => setStatus(null));
    }, [client]);

    /**
     * Opening it is reading it, as in Correo.
     *
     * Once per panel. The mailbox's version of this effect re-arms on the flag
     * it just restored when the write fails, which retries for as long as the
     * failure lasts; one attempt per opening is what a person does.
     */
    const markedRead = useRef(false);
    useEffect(() => {
        if (!thread?.unread || markedRead.current) return;
        markedRead.current = true;
        setThread(t => (t ? { ...t, unread: false } : t));
        markThreadRead(client, threadId)
            .then(() => onChanged?.())
            .catch(() => setThread(t => (t ? { ...t, unread: true } : t)));
    }, [thread?.unread, client, threadId, onChanged]);

    // Who and what it is about, by name. Read here rather than passed in
    // because the panel opens from screens that know neither.
    const entityType = thread ? entityTypeOf(thread) : "none";
    const entityId = thread?.entity_id ?? null;
    const jobId = entityIdOf(thread?.job_id);

    useEffect(() => {
        if (!data || !entityId || (entityType !== "talent" && entityType !== "company")) {
            setLinkedName(null);
            return;
        }
        let live = true;
        data.collection(entityType === "talent" ? "talents" : "companies").findById(entityId)
            .then((row: any) => {
                if (!live) return;
                const name = entityType === "talent" ? row?.full_name : row?.name;
                setLinkedName(String(name ?? "").trim() || null);
            })
            .catch(() => { if (live) setLinkedName(null); });
        return () => { live = false; };
    }, [data, entityType, entityId]);

    useEffect(() => {
        if (!data || !jobId) {
            setVacancyTitle(null);
            return;
        }
        let live = true;
        data.collection("jobs").findById(jobId)
            .then((row: any) => { if (live) setVacancyTitle(String(row?.title ?? "").trim() || null); })
            .catch(() => { if (live) setVacancyTitle(null); });
        return () => { live = false; };
    }, [data, jobId]);

    /** One field on the thread, written and mirrored. The change on screen is the confirmation. */
    const patch = useCallback(async (next: Partial<ThreadRow>) => {
        if (!data) return;
        setWorking(true);
        try {
            await data.collection("email-threads").update(threadId, next);
            setThread(t => (t ? { ...t, ...next } : t));
            onChanged?.();
        } catch (err: any) {
            snackbar.open({ type: "error", message: err?.message || "No se pudo guardar" });
        } finally {
            setWorking(false);
        }
    }, [data, threadId, onChanged, snackbar]);

    const replyTarget = useMemo<ComposerTarget | null>(() => {
        if (!thread) return null;
        const firstInbound = (messages ?? []).find(m => m.direction === "inbound" && (m.from_name ?? "").trim());
        const type = entityTypeOf(thread);
        return {
            to: (thread.participants ?? []).filter(Boolean),
            subject: "",
            threadId: thread.id,
            // The From header's display name is the best name there is for
            // the person on the other end — the same rule Correo uses.
            recipientName: firstInbound?.from_name ?? linkedName,
            draftContext: {
                thread_id: thread.id,
                entity_type: type === "none" ? undefined : type,
                entity_id: thread.entity_id ?? undefined,
                employer_key: thread.employer_key ?? undefined,
                contact_id: thread.contact_id ?? undefined
            }
        };
    }, [thread, messages, linkedName]);

    const openInCorreo = useCallback(() => {
        urls.navigate(conversationUrl(threadId));
        // For when Correo is the screen underneath and will not remount — see
        // OPEN_CONVERSATION_EVENT. Anywhere else nobody is listening yet, and
        // the mailbox reads the address when it mounts.
        window.dispatchEvent(new CustomEvent(OPEN_CONVERSATION_EVENT, { detail: threadId }));
    }, [urls, threadId]);

    const title = thread ? threadTitle(thread) : "Conversación";
    const gmailUrl = gmailThreadUrl(thread?.gmail_thread_id);
    const waiting = thread ? daysWaiting(thread) : null;
    const count = thread?.message_count ?? messages?.length ?? 0;

    return (
        <div className="relative flex flex-col h-full w-full bg-surface-card">
            {liveRegion}

            {/* The record panels' identity bar, in the same places: close on
                the seam with the screen behind, what this is, and the way to
                the full-screen version of it at the other end. */}
            <div className="h-[52px] shrink-0 flex items-center gap-2 pl-1.5 pr-2 bg-surface-sheet">
                <IconButton className="self-center" size="small" aria-label="Cerrar" onClick={() => sideDialogs.close()}>
                    <XIcon />
                </IconButton>
                <Typography variant="caption" className="text-text-disabled dark:text-text-disabled-dark whitespace-nowrap hidden sm:block">
                    Correo&nbsp;/
                </Typography>
                <span className="font-headers font-semibold text-[15px] tracking-tight truncate min-w-0" title={title}>
                    {title}
                </span>
                <div className="flex-1" />
                <Tooltip title="Abrir en Correo" asChild>
                    <IconButton size="small" aria-label="Abrir en Correo" onClick={openInCorreo}>
                        <Maximize2Icon size={iconSize.smallest} />
                    </IconButton>
                </Tooltip>
            </div>

            <div className="flex-1 overflow-y-auto">
                <div className="p-6 max-w-3xl flex flex-col gap-5">
                    {error && <Alert color="error" size="small">{error}</Alert>}

                    {thread === undefined && !error && <CircularProgressCenter />}

                    {thread === null && (
                        <Typography variant="body1" color="secondary">
                            Esta conversación ya no existe.
                        </Typography>
                    )}

                    {thread && (
                        <>
                            <div className="flex flex-col gap-2">
                                <Typography variant="h5" component="h2">{threadTitle(thread)}</Typography>
                                <Typography variant="body2" color="secondary">
                                    {threadWith(thread)} · {count} {plural(count, "mensaje", "mensajes")} · {sinceLabel(thread.last_message_at)}
                                </Typography>
                                <div className="flex items-center gap-1.5 flex-wrap">
                                    {waiting !== null && (
                                        <Chip size="small" colorScheme={waiting >= 7 ? "redLighter" : "orangeLighter"}>
                                            {waiting === 0 ? "esperan hoy" : `esperan ${waiting} día${waiting === 1 ? "" : "s"}`}
                                        </Chip>
                                    )}
                                    {entityType !== "none" && (
                                        <Chip size="small" colorScheme={ENTITY_COLOR[entityType]}>{ENTITY_LABEL[entityType]}</Chip>
                                    )}
                                    {!isPersonThread(thread) && (
                                        <Chip size="small" colorScheme="grayLighter">{KIND_LABEL[kindOf(thread)]}</Chip>
                                    )}
                                    {thread.status === "closed" && (
                                        <Chip size="small" colorScheme="grayLighter">Archivada</Chip>
                                    )}
                                </div>
                                {(entityType === "talent" || entityType === "company") && entityId && (
                                    <Typography variant="body2">
                                        <UserIcon size={iconSize.smallest} className="inline mr-1 opacity-60" />
                                        <EntityLink kind={entityType === "talent" ? "talent" : "company"} id={entityId}>
                                            {linkedName || (entityType === "talent" ? "ver la ficha del candidato" : "ver la ficha de la empresa")}
                                        </EntityLink>
                                    </Typography>
                                )}
                                {jobId && (
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Chip size="small" colorScheme="purpleLighter">
                                            {thread.application_id ? "Candidatura" : "Vacante"}
                                        </Chip>
                                        <Typography variant="body2" color="secondary">
                                            <EntityLink kind="job" id={jobId}>{vacancyTitle ?? "vacante"}</EntityLink>
                                            {!thread.application_id && " · reconocida en el asunto"}
                                        </Typography>
                                    </div>
                                )}
                            </div>

                            {/* Correo's own buttons, minus the ones that are
                                about the mailbox rather than this person. */}
                            <div className="flex gap-2 flex-wrap items-center">
                                {thread.status === "closed" ? (
                                    <Tooltip title="Vuelve a la bandeja del panel" asChild>
                                        <Button size="small" variant="text" disabled={working} onClick={() => patch({ status: "open" })}>
                                            <UnarchiveIcon size={iconSize.smallest} />
                                            Sacar del archivo
                                        </Button>
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="La quita de la bandeja del panel. En Gmail no cambia nada." asChild>
                                        <Button size="small" variant="text" disabled={working} onClick={() => patch({ status: "closed" })}>
                                            <ArchiveIcon size={iconSize.smallest} />
                                            Archivar
                                        </Button>
                                    </Tooltip>
                                )}
                                {gmailUrl && (
                                    <Button component="a" size="small" variant="text" href={gmailUrl} target="_blank" rel="noopener noreferrer">
                                        <ExternalLinkIcon size={iconSize.smallest} />
                                        Abrir en Gmail
                                    </Button>
                                )}
                                {isPersonThread(thread) && thread.status !== "closed" && (
                                    thread.needs_reply === false ? (
                                        <Button size="small" variant="text" disabled={working} onClick={() => patch({ needs_reply: true })}>
                                            <ReplyIcon size={iconSize.smallest} />
                                            Sigue pendiente
                                        </Button>
                                    ) : (
                                        <Button size="small" variant="text" disabled={working} onClick={() => patch({ needs_reply: false })}>
                                            <CheckIcon size={iconSize.smallest} />
                                            No hace falta contestar
                                        </Button>
                                    )
                                )}
                            </div>

                            <Separator />

                            {messages === null
                                ? <CircularProgressCenter />
                                : <EmailThreadView messages={messages} client={client} />}

                            {/* Under the conversation, where Correo puts it:
                                you read to the end and then you write. */}
                            {replying && replyTarget ? (
                                <EmailComposer
                                    client={client}
                                    target={replyTarget}
                                    canSend={status?.sending !== false}
                                    onSent={async () => {
                                        setReplying(false);
                                        await load();
                                        onChanged?.();
                                        snackbar.open({ type: "success", message: "Enviado" });
                                    }}
                                    onCancel={() => setReplying(false)}
                                />
                            ) : (
                                <div>
                                    <Button size="small" variant="outlined" onClick={() => setReplying(true)}>
                                        <ReplyIcon size={iconSize.smallest} />
                                        Responder
                                    </Button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
